import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  MapPinIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon
} from '@heroicons/react/24/outline';
import JobCard from './JobCard';

import { API_BASE } from '../lib/config';
const API = `${API_BASE}`;

const AreaSearchModal = ({ isOpen, onClose, onSelectArea }) => {
  const [step, setStep] = useState(1); // 1: 都道府県選択, 2: 市区町村選択, 3: 求人一覧
  const [prefectures, setPrefectures] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedPrefecture, setSelectedPrefecture] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    hourly_rate_sort: '',
    work_date: '',
    experience_required: null,
    short_hours: false,
    same_day_payment: false
  });

  useEffect(() => {
    if (isOpen && step === 1) {
      fetchPrefectures();
    }
  }, [isOpen, step]);

  const fetchPrefectures = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/areas/prefectures`);
      setPrefectures(response.data.prefectures);
    } catch (error) {
      toast.error('都道府県データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (prefecture) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/areas/cities/${prefecture}`);
      setCities(response.data.cities);
      setSelectedPrefecture(prefecture);
      setStep(2);
    } catch (error) {
      toast.error('市区町村データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobsByArea = async (prefecture, city) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      // フィルターパラメータを追加
      if (filters.hourly_rate_sort) params.append('hourly_rate_sort', filters.hourly_rate_sort);
      if (filters.work_date) params.append('work_date', filters.work_date);
      if (filters.experience_required !== null) params.append('experience_required', filters.experience_required);
      if (filters.short_hours) params.append('short_hours', 'true');
      if (filters.same_day_payment) params.append('same_day_payment', 'true');

      const response = await axios.get(`${API}/areas/jobs/${prefecture}/${city}?${params.toString()}`);
      setJobs(response.data);
      setSelectedCity(city);
      setStep(3);

      // 地域選択履歴を保存
      await saveAreaPreference(prefecture, city);
      
      if (onSelectArea) {
        onSelectArea({ prefecture, city, jobs: response.data });
      }
    } catch (error) {
      toast.error('求人データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const saveAreaPreference = async (prefecture, city) => {
    try {
      await axios.post(`${API}/users/area-preference`, {
        prefecture,
        city
      });
    } catch (error) {
      console.error('Failed to save area preference:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    if (selectedPrefecture && selectedCity) {
      fetchJobsByArea(selectedPrefecture, selectedCity);
    }
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      hourly_rate_sort: '',
      work_date: '',
      experience_required: null,
      short_hours: false,
      same_day_payment: false
    });
  };

  const goBack = () => {
    if (step === 2) {
      setStep(1);
      setCities([]);
      setSelectedPrefecture('');
    } else if (step === 3) {
      setStep(2);
      setJobs([]);
      setSelectedCity('');
    }
  };

  const reset = () => {
    setStep(1);
    setPrefectures([]);
    setCities([]);
    setJobs([]);
    setSelectedPrefecture('');
    setSelectedCity('');
    setSearchTerm('');
    clearFilters();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const filteredPrefectures = prefectures.filter(pref =>
    pref.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCities = cities.filter(city =>
    city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center">
            {step > 1 && (
              <button onClick={goBack} className="mr-3 p-1">
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-lg font-semibold">
              {step === 1 && '都道府県を選択'}
              {step === 2 && `${selectedPrefecture} の市区町村`}
              {step === 3 && `${selectedPrefecture} ${selectedCity} の求人`}
            </h2>
          </div>
          <button onClick={handleClose} className="p-1">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Search Bar */}
        {(step === 1 || step === 2) && (
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={step === 1 ? "都道府県を検索..." : "市区町村を検索..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Filters for Jobs */}
        {step === 3 && (
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
              フィルター
            </button>
            
            {showFilters && (
              <div className="mt-4 space-y-3 bg-gray-50 p-3 rounded-lg">
                {/* 時給ソート */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">時給</label>
                  <select
                    value={filters.hourly_rate_sort}
                    onChange={(e) => handleFilterChange('hourly_rate_sort', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">指定なし</option>
                    <option value="desc">高い順</option>
                    <option value="asc">安い順</option>
                  </select>
                </div>

                {/* 勤務日 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">勤務日</label>
                  <select
                    value={filters.work_date}
                    onChange={(e) => handleFilterChange('work_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">指定なし</option>
                    <option value="today">今日</option>
                    <option value="tomorrow">明日</option>
                  </select>
                </div>

                {/* チェックボックス */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={filters.experience_required === false}
                      onChange={(e) => handleFilterChange('experience_required', e.target.checked ? false : null)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    未経験OK
                  </label>
                  
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={filters.short_hours}
                      onChange={(e) => handleFilterChange('short_hours', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    短時間OK（3時間以内）
                  </label>
                  
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={filters.same_day_payment}
                      onChange={(e) => handleFilterChange('same_day_payment', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 mr-2"
                    />
                    即日払いあり
                  </label>
                </div>

                <div className="flex space-x-2 pt-2">
                  <button
                    onClick={applyFilters}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700"
                  >
                    適用
                  </button>
                  <button
                    onClick={clearFilters}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md text-sm font-medium hover:bg-gray-400"
                  >
                    クリア
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: step === 3 ? '400px' : '500px' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Step 1: 都道府県選択 */}
              {step === 1 && (
                <div className="p-4">
                  <div className="space-y-2">
                    {filteredPrefectures.map((prefecture) => (
                      <button
                        key={prefecture}
                        onClick={() => fetchCities(prefecture)}
                        className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <span className="font-medium">{prefecture}</span>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: 市区町村選択 */}
              {step === 2 && (
                <div className="p-4">
                  <div className="space-y-2">
                    {filteredCities.map((city) => (
                      <button
                        key={city}
                        onClick={() => fetchJobsByArea(selectedPrefecture, city)}
                        className="w-full flex items-center justify-between p-3 text-left border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <span className="font-medium">{city}</span>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 3: 求人一覧 */}
              {step === 3 && (
                <div className="p-4">
                  {jobs.length === 0 ? (
                    <div className="text-center py-12">
                      <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">求人が見つかりませんでした</h3>
                      <p className="text-gray-600 mb-4">別の地域を選択するか、フィルターを変更してみてください。</p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-4 text-sm text-gray-600">
                        {jobs.length}件の求人が見つかりました
                      </div>
                      <div className="space-y-4">
                        {jobs.map((job) => (
                          <JobCard
                            key={job.id}
                            job={job}
                            compact={true}
                            onClick={() => {
                              handleClose();
                              window.open(`/jobs/${job.id}`, '_blank');
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AreaSearchModal;